'use client';

import { useMemo, useState } from 'react';

import { cn } from '@kit/ui/utils';

import type { Chapter45Anchor, Chapter45Block } from '../../chapter45';
import { parseChapter45 } from '../../chapter45';
import { ContentBlock } from './content-block';
import { HighlightedText } from './highlighted-text';
import { useScrollTracking } from './hooks/use-scroll-tracking';
import { buildRangesMap, computeQuoteFirstLast } from './utils';

// ============================================================================
// Section Types
// ============================================================================

interface Section {
  id: string;
  depth: number;
  headingBlock?: Chapter45Block & { type: 'heading' };
  contentBlocks: Chapter45Block[];
  isMainTitle?: boolean;
}

// ============================================================================
// Semantic Thread Components
// ============================================================================

interface ThreadNodeProps {
  depth: number;
  isActive: boolean;
  level?: 1 | 2 | 3 | 4;
}

function ThreadNode({ depth, isActive, level: _level }: ThreadNodeProps) {
  const size =
    depth === 0 ? 'h-4 w-4' : depth === 1 ? 'h-3 w-3' : 'h-2.5 w-2.5';

  return (
    <div
      className={cn(
        'absolute top-1.5 -translate-x-1/2 rounded-full transition-all duration-300 ease-out',
        depth === 0 && 'left-0',
        depth === 1 && '-left-2',
        depth === 2 && '-left-4',
        size,
        isActive
          ? 'scale-105 bg-zinc-500 dark:bg-zinc-400'
          : 'bg-zinc-300 dark:bg-zinc-600',
      )}
    />
  );
}

interface ThreadSectionProps {
  section: Section;
  anchorsByBlockId: Map<string, Chapter45Anchor[]>;
  firstAnchors: Set<string>;
  lastAnchors: Set<string>;
  isActive: boolean;
  sectionRef: (el: HTMLElement | null) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function ThreadSection({
  section,
  anchorsByBlockId,
  firstAnchors,
  lastAnchors,
  isActive,
  sectionRef,
  onMouseEnter,
  onMouseLeave,
}: ThreadSectionProps) {
  const headingBlock = section.headingBlock;

  const headingClassName = headingBlock
    ? headingBlock.level === 1
      ? 'text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50'
      : headingBlock.level === 2
        ? 'text-lg font-bold text-zinc-800 dark:text-zinc-100'
        : headingBlock.level === 3
          ? 'text-[15px] font-semibold text-zinc-700 dark:text-zinc-200'
          : 'text-sm font-medium text-zinc-600 dark:text-zinc-300'
    : '';

  const Tag = headingBlock
    ? headingBlock.level === 1
      ? 'h1'
      : headingBlock.level === 2
        ? 'h2'
        : headingBlock.level === 3
          ? 'h3'
          : 'h4'
    : 'div';

  return (
    <section
      ref={sectionRef}
      data-section-id={section.id}
      data-section-depth={section.depth}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'semantic-thread-section relative transition-all duration-300 ease-out',
        section.depth === 0 && 'ml-0 pl-5',
        section.depth === 1 && 'ml-6 pl-3',
        section.depth === 2 && 'ml-12 pl-1',
        isActive &&
          'rounded-r-lg bg-gradient-to-r from-zinc-100/70 to-transparent dark:from-zinc-800/40',
      )}
    >
      <div
        className={cn(
          'absolute top-0 bottom-0 -translate-x-1/2 transition-all duration-300',
          section.depth === 0 && 'left-0',
          section.depth === 1 && '-left-2',
          section.depth === 2 && '-left-4',
          isActive
            ? 'w-0.5 bg-zinc-400 dark:bg-zinc-500'
            : 'w-px bg-zinc-200 dark:bg-zinc-700',
        )}
      />

      {headingBlock && (
        <ThreadNode
          depth={section.depth}
          isActive={isActive}
          level={headingBlock.level}
        />
      )}

      <div className="relative">
        {headingBlock && (
          <Tag
            data-ch45-blockid={headingBlock.blockId}
            className={cn(
              headingClassName,
              'mt-6 mb-3 transition-colors duration-300 first:mt-0',
              isActive && 'text-zinc-900 dark:text-zinc-50',
            )}
          >
            <HighlightedText
              text={headingBlock.text}
              ranges={anchorsByBlockId.get(headingBlock.blockId)}
              firstAnchors={firstAnchors}
              lastAnchors={lastAnchors}
            />
          </Tag>
        )}

        {section.contentBlocks.map((block, index) => (
          <ContentBlock
            key={block.blockId}
            block={block}
            anchorsByBlockId={anchorsByBlockId}
            firstAnchors={firstAnchors}
            lastAnchors={lastAnchors}
            prevBlockType={section.contentBlocks[index - 1]?.type}
          />
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// Section Grouping
// ============================================================================

function groupBlocksIntoSections(blocks: Chapter45Block[]): Section[] {
  const sections: Section[] = [];
  let currentSection: Section | null = null;

  for (const block of blocks) {
    if (block.type === 'heading') {
      const isMainTitle = block.level === 1;
      const depth = block.level <= 2 ? 0 : block.level === 3 ? 1 : 2;

      currentSection = {
        id: block.blockId,
        depth,
        headingBlock: block,
        contentBlocks: [],
        isMainTitle,
      };
      sections.push(currentSection);
    } else {
      if (currentSection) {
        currentSection.contentBlocks.push(block);
      } else {
        currentSection = {
          id: `root-${sections.length}`,
          depth: 0,
          contentBlocks: [block],
        };
        sections.push(currentSection);
      }
    }
  }

  return sections;
}

// ============================================================================
// Chapter45Blocks Component
// ============================================================================

interface Chapter45BlocksProps {
  blocks: Chapter45Block[];
  anchorsByBlockId: Map<string, Chapter45Anchor[]>;
  firstAnchors: Set<string>;
  lastAnchors: Set<string>;
}

function Chapter45Blocks({
  blocks,
  anchorsByBlockId,
  firstAnchors,
  lastAnchors,
}: Chapter45BlocksProps) {
  const sections = useMemo(() => groupBlocksIntoSections(blocks), [blocks]);

  const [isTouchDevice] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error - msMaxTouchPoints is IE-specific
      navigator.msMaxTouchPoints > 0
    );
  });

  const {
    containerRef,
    setSectionRef,
    setHoveredSectionId,
    currentActiveSectionId,
  } = useScrollTracking({ isTouchDevice, sections });

  const mainTitleSection = sections.find((s) => s.isMainTitle);
  const threadSections = sections.filter((s) => !s.isMainTitle);

  return (
    <div
      ref={containerRef}
      className="semantic-thread-container relative min-h-full py-6 pl-2"
    >
      {mainTitleSection && (
        <div className="mb-6 -ml-2">
          {mainTitleSection.headingBlock && (
            <h1 className="mb-3 text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              <HighlightedText
                text={mainTitleSection.headingBlock.text}
                ranges={anchorsByBlockId.get(
                  mainTitleSection.headingBlock.blockId,
                )}
                firstAnchors={firstAnchors}
                lastAnchors={lastAnchors}
              />
            </h1>
          )}
          {mainTitleSection.contentBlocks.map((block, index) => (
            <ContentBlock
              key={block.blockId}
              block={block}
              anchorsByBlockId={anchorsByBlockId}
              firstAnchors={firstAnchors}
              lastAnchors={lastAnchors}
              prevBlockType={mainTitleSection.contentBlocks[index - 1]?.type}
            />
          ))}
        </div>
      )}

      <div className="space-y-1">
        {threadSections.map((section) => (
          <ThreadSection
            key={section.id}
            section={section}
            anchorsByBlockId={anchorsByBlockId}
            firstAnchors={firstAnchors}
            lastAnchors={lastAnchors}
            isActive={currentActiveSectionId === section.id}
            sectionRef={setSectionRef(section.id)}
            onMouseEnter={
              !isTouchDevice ? () => setHoveredSectionId(section.id) : undefined
            }
            onMouseLeave={
              !isTouchDevice ? () => setHoveredSectionId(null) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Chapter45Content Component
// ============================================================================

interface Chapter45ContentProps {
  content: string;
  anchors: Chapter45Anchor[];
}

export function Chapter45Content({ content, anchors }: Chapter45ContentProps) {
  const blocks = useMemo(() => parseChapter45(content), [content]);
  const anchorsByBlockId = useMemo(() => buildRangesMap(anchors), [anchors]);
  const { firstAnchors, lastAnchors } = useMemo(
    () => computeQuoteFirstLast(anchors),
    [anchors],
  );

  return (
    <Chapter45Blocks
      blocks={blocks}
      anchorsByBlockId={anchorsByBlockId}
      firstAnchors={firstAnchors}
      lastAnchors={lastAnchors}
    />
  );
}
